import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CommissionTransaction {
  id: string;
  transactionDate: string | null;
  lenderName: string;
  lenderAccountNumber: string;
  commissionType: string;
  commissionAmount: string;
  gstAmount: string | null;
  totalAmount: string;
  processingStatus: string;
  matchedPartnerId: string | null;
}

interface PartnerCommissionsPanelProps {
  partnerId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-gray-500/10 text-gray-700 dark:text-gray-400",
  imported: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
  matched: "bg-purple-500/10 text-purple-700 dark:text-purple-400",
  calculated: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
  paid: "bg-green-500/10 text-green-700 dark:text-green-400",
  disputed: "bg-red-500/10 text-red-700 dark:text-red-400",
  void: "bg-slate-500/10 text-slate-700 dark:text-slate-400",
};

function formatCurrency(value: string | null | undefined): string {
  if (!value) return "—";
  const num = parseFloat(value);
  if (isNaN(num)) return "—";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
  }).format(num);
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("en-AU", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return value;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PartnerCommissionsPanel({ partnerId }: PartnerCommissionsPanelProps) {
  // Default queryFn converts the object segment to query params:
  // queryKey = ["/api/commissions/transactions", { partnerId }]
  // → GET /api/commissions/transactions?partnerId=<value>
  const { data: transactions = [], isLoading } = useQuery<CommissionTransaction[]>({
    queryKey: ["/api/commissions/transactions", { partnerId }],
    enabled: !!partnerId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="panel-partner-commissions">
      {transactions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center border rounded-md">
          <p className="text-sm text-muted-foreground">No commission transactions for this partner</p>
          <p className="text-xs text-muted-foreground mt-1">
            Matched transactions will appear here once imported
          </p>
        </div>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow className="h-10 bg-muted/50">
                <TableHead className="text-xs font-medium px-2 py-1">Date</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Lender</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Account #</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Type</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1 text-right">Amount</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1 text-right">GST</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1 text-right">Total</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((tx) => (
                <TableRow key={tx.id} className="h-10" data-testid={`row-transaction-${tx.id}`}>
                  <TableCell className="px-2 py-1 text-sm">{formatDate(tx.transactionDate)}</TableCell>
                  <TableCell className="px-2 py-1 text-sm">{tx.lenderName}</TableCell>
                  <TableCell className="px-2 py-1 text-sm font-mono text-xs">{tx.lenderAccountNumber}</TableCell>
                  <TableCell className="px-2 py-1 text-sm capitalize">{tx.commissionType}</TableCell>
                  <TableCell className="px-2 py-1 text-sm text-right">{formatCurrency(tx.commissionAmount)}</TableCell>
                  <TableCell className="px-2 py-1 text-sm text-right">{formatCurrency(tx.gstAmount)}</TableCell>
                  <TableCell className="px-2 py-1 text-sm text-right">{formatCurrency(tx.totalAmount)}</TableCell>
                  <TableCell className="px-2 py-1">
                    <Badge
                      variant="secondary"
                      className={`text-xs capitalize ${STATUS_COLORS[tx.processingStatus] ?? ""}`}
                      data-testid={`status-badge-${tx.processingStatus}`}
                    >
                      {tx.processingStatus}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
